/**
 * Mobile (below `lg`) view of the PO / discount-code box, with the flexible-dates checkbox and
 * the terms-of-sale text below. Stateless: `PurchaseOrderDiscountCode.tsx` holds the logic.
 * Differences from the desktop view: the PO input is always marked required, the Apply button
 * shows whenever the sales plant allows coupons (whatever the checkout type), and a failed PO
 * validation scrolls the PO input into view below the sticky header.
 */
import React, { useEffect } from "react";
import { Button } from "@ryerson/frontend.button";
import { Typography } from "@ryerson/frontend.typography";
import { Checkbox, Input, ErpInput } from "@ryerson/frontend.form-controls";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useTheme } from "@ryerson/frontend.theme";
import { validateErpRestrictedCharacters } from "@ryerson/frontend.common";
import { PurchaseOrderProps } from "./PurchaseOrderDiscountCode";
import { Flex } from "@ryerson/frontend.layout";
import { useApplication } from "@ryerson/frontend.application";
import { VerifiedAndSecured } from "@ryerson/frontend.assets";
import { Link } from "@ryerson/frontend.navigation";
import { Alert } from "@ryerson/frontend.notification";
import { CheckOutType, useEcommerce } from "@ryerson/frontend.e-commerce";
import { MAX_FIELD_LENGTHS } from "@ryerson/frontend.common";
import { useFeatureToggles } from "@ryerson/frontend.feature-toggles";
import { maxDiscountCodeLength } from "@components/constants";

const FlexibleDateSection = styled(Flex)`
	width: 100%;
	padding: 20px;
	${(props: any) => {
		const { backgroundColor } = props;
		return css`
			border-radius: 0px 0px 2px 2px;
			background-color: ${backgroundColor};
		`;
	}};
`;

const GrayBox = styled(Flex)`
	width: 100%;
	padding: 20px;
	border-radius: 2px 2px 0 0;
	${(props: any) => {
		const { bg } = props;
		return css`
			background-color: ${bg};
		`;
	}};
`;

const captionText = css`
	width: 50%;
`;

const FlexRow = styled(Flex)`
	padding: 10px 0;
`;

const ApplyButton = styled(Button)`
	padding-top: 10px;
`;

const OrderInput = styled(Input)`
	width: 50%;
`;

const ErpOrderInput = styled(ErpInput)`
	width: 50%;
`;

const LegalTextContainer = styled(Flex)`
	margin-bottom: 10px;
	padding: 30px 25px;
	border-radius: 0 0 2px 2px;
	${(props: any) => {
		const { backgroundColor } = props;
		return css`
			background: ${backgroundColor};
		`;
	}};
`;

/** Mobile PO / discount-code box. */
const PurchaseOrderDiscountCodeMobile: React.FC<PurchaseOrderProps> = ({
	updateDiscountCode,
	currentDiscountCode,
	setCurrentDiscountCode,
	cartData,
	content,
	showDiscountMessage,
	discountMessage,
	setCurrentPurchaseOrderNumber,
	currentPurchaseOrderNumber,
	showFlexDates,
	testingId,
	flexDates,
	validationTriggered,
	purchaseOrderRequired,
	isSubmitted,
	POInputRef,
	isCouponAllowed = false,
}) => {
	const { theme } = useTheme();
	const { lighterGray, darkGray, secondaryBrand, background, text, gray, error } =
		theme.colors.primary;
	const { checkoutType } = useEcommerce();
	const {
		localization: { language },
	} = useApplication();
	const { isFeatureEnabled } = useFeatureToggles();

	const isValid =
		!validationTriggered ||
		(currentPurchaseOrderNumber?.length > 0 &&
			!validateErpRestrictedCharacters(currentPurchaseOrderNumber));

	useEffect(() => {
		if (validationTriggered && !isValid) {
			const viewportTop = window.scrollY;
			const stickyHeader = document.getElementsByClassName("sticky")[0];
			const elementPosition =
				(document as any)?.getElementById("erpOrderInput")?.getBoundingClientRect().top +
				viewportTop;
			const headerHeight = stickyHeader !== undefined ? 120 : 155;

			const scrollToPosition = elementPosition - headerHeight;

			window?.scrollTo({ behavior: "smooth", left: 0, top: scrollToPosition });
		}
	}, [validationTriggered]);
	return (
		<Flex direction="column">
			<GrayBox direction="column" {...{ bg: lighterGray }}>
				<FlexRow justifyContent="space-between" alignItems="center" itemRef={POInputRef}>
					<Typography size="md" color={secondaryBrand} css={captionText}>
						{content.purchaseOrder}
						{purchaseOrderRequired && <Typography color={error}>&nbsp;*</Typography>}
					</Typography>
					<ErpOrderInput
						id="erpOrderInput"
						value={currentPurchaseOrderNumber}
						inputType="text"
						shape="rounded"
						size="sm"
						onChange={(e) => {
							setCurrentPurchaseOrderNumber(
								e.target.value.substring(0, MAX_FIELD_LENGTHS.HEADER_PO)
							);
						}}
						css={{
							marginTop:
								validationTriggered && currentPurchaseOrderNumber?.length === 0
									? 40
									: 0,
						}}
						required={true}
						language={language}
						disabled={cartData?.length === 0}
						dataTestId="purchase-order-input"
						submitted={isSubmitted}
						isValid={isValid}
						validation={validateErpRestrictedCharacters}
					/>
				</FlexRow>
				{isCouponAllowed &&
					(checkoutType === CheckOutType.CART || checkoutType === CheckOutType.QUOTE) && (
						<FlexRow
							css={{ marginLeft: showDiscountMessage ? "-10px" : "0" }}
							justifyContent="space-between"
							alignItems="center"
						>
							{showDiscountMessage ? (
								<Alert
									severity={
										discountMessage === content.discountApplied
											? "success"
											: "error"
									}
									size="sm"
								>
									<Typography size="sm">{discountMessage}</Typography>
								</Alert>
							) : (
								<Typography size="md" color={secondaryBrand} css={captionText}>
									{content.discountCode}
								</Typography>
							)}
							<OrderInput
								value={currentDiscountCode}
								inputType="text"
								shape="rounded"
								size="sm"
								onChange={(e) => {
									setCurrentDiscountCode(e.target.value);
								}}
								language={language}
								disabled={cartData?.length === 0}
								dataTestId="discount-code-input"
								maxLength={maxDiscountCodeLength}
							/>
						</FlexRow>
					)}
				{isCouponAllowed && (
					<ApplyButton
						onClick={() => updateDiscountCode(false)}
						label={content.apply}
						shape="rounded"
						size="sm"
						type="secondary"
						background={darkGray}
						variant="outline"
						paddingType="relaxed"
						fullwidth={true}
						disabled={false}
					/>
				)}
			</GrayBox>
			{checkoutType !== CheckOutType.QUOTE && showFlexDates && (
				<FlexibleDateSection
					alignItems="center"
					justifyContent="space-between"
					{...{ backgroundColor: darkGray }}
				>
					<Flex alignItems="center">
						<Checkbox
							id={testingId}
							value={content.flexibleDates}
							onChange={() => updateDiscountCode(true)}
							type="tertiary"
							checked={flexDates}
							size="md"
							language={language}
						/>
					</Flex>
				</FlexibleDateSection>
			)}
			<LegalTextContainer {...{ backgroundColor: text }} direction="column">
				<VerifiedAndSecured onClick={() => {}} color={gray} width="125px" />
				<Typography css={{ paddingTop: "10px" }} size="md" color={background}>
					{content.legalTextOne}
					<Link to={window?.location.origin + "/terms-and-conditions/"}>
						{content.legalTextLink}
					</Link>
					{content.legalTextTwo}
				</Typography>
			</LegalTextContainer>
		</Flex>
	);
};

export default PurchaseOrderDiscountCodeMobile;
